/**
 * BuffSystem - Temporary stat modifiers and effects
 *
 * Buffs: Positive effects (damage boost, speed, regen, etc.)
 * Debuffs: Negative effects (damage reduction, slow, etc.)
 *
 * Can come from: Skills, items, potions, auras, triggers
 */

import { Modifier, createModifier } from '../modifiers/ModifierSystem';
import { debugWarn } from '@shared/utils/Logger';

// ========================
// Buff Types
// ========================

export type BuffType =
  // Offensive buffs
  | 'damage_boost'      // Increased damage
  | 'crit_boost'        // Increased crit chance/damage
  | 'attack_speed'      // Increased attack speed
  | 'cast_speed'        // Increased cast speed
  | 'penetration'       // Resistance penetration

  // Defensive buffs
  | 'armor_boost'       // Increased armor
  | 'resistance_boost'  // Increased resistances
  | 'damage_reduction'  // Flat damage reduction
  | 'shield'            // Temporary shield (absorbs damage)
  | 'invulnerable'      // Cannot take damage

  // Utility buffs
  | 'speed_boost'       // Movement speed
  | 'hp_regen'          // Health regeneration
  | 'mana_regen'        // Mana regeneration
  | 'leech_boost'       // Increased leech

  // Debuffs (negative)
  | 'weakness'          // Reduced damage dealt
  | 'vulnerability'     // Increased damage taken
  | 'curse'             // Various negative effects
  | 'silence'           // Cannot use skills
  | 'blind'             // Reduced accuracy;

// ========================
// Buff Definition
// ========================

export interface Buff {
  id: string;
  type: BuffType;
  name: string;
  description: string;
  icon?: string;

  // Source tracking
  sourceId: string;
  sourceType: 'skill' | 'item' | 'potion' | 'aura' | 'trigger' | 'enemy';

  // Timing
  duration: number;      // Total duration (-1 for permanent until removed)
  remaining: number;     // Time left
  tickInterval?: number; // For periodic effects (regen)
  tickTimer?: number;

  // Effect values
  value: number;         // Primary value (% or flat depending on type)
  flatValue?: number;    // Secondary flat value
  element?: string;      // For elemental-specific buffs

  // Stacking
  stacks: number;
  maxStacks: number;
  stackMode: 'refresh' | 'stack' | 'independent'; // How multiple applications work

  // Modifiers this buff applies
  modifiers: Modifier[];

  // Visual
  isDebuff: boolean;
  color?: string;
}

// ========================
// Buff Templates
// ========================

export interface BuffTemplate {
  type: BuffType;
  name: string;
  description: string;
  icon?: string;
  duration: number;
  value: number;
  flatValue?: number;
  maxStacks: number;
  stackMode: 'refresh' | 'stack' | 'independent';
  isDebuff: boolean;
  color?: string;
  tickInterval?: number;
  createModifiers: (value: number, stacks: number, flatValue?: number) => Modifier[];
}

// Predefined buff templates
export const BUFF_TEMPLATES: Record<string, BuffTemplate> = {
  // Offensive
  damage_boost: {
    type: 'damage_boost',
    name: 'Hasar Artışı',
    description: '%{value} artan hasar',
    duration: 10,
    value: 20,
    maxStacks: 1,
    stackMode: 'refresh',
    isDebuff: false,
    color: '#ff6600',
    createModifiers: (value) => [
      createModifier('damage', 'increased', value, 'buff_damage_boost'),
    ],
  },

  berserker_rage: {
    type: 'damage_boost',
    name: 'Berserker Öfkesi',
    description: '%{value} artan hasar, %{value/2} artan saldırı hızı',
    duration: 8,
    value: 50,
    maxStacks: 1,
    stackMode: 'refresh',
    isDebuff: false,
    color: '#ff0000',
    createModifiers: (value) => [
      createModifier('damage', 'increased', value, 'buff_berserker'),
      createModifier('attackSpeed', 'increased', Math.floor(value / 2), 'buff_berserker'),
    ],
  },

  crit_boost: {
    type: 'crit_boost',
    name: 'Keskin Bakış',
    description: '+{value}% kritik şansı',
    duration: 8,
    value: 15,
    maxStacks: 3,
    stackMode: 'stack',
    isDebuff: false,
    color: '#ffff00',
    createModifiers: (value, stacks) => [
      createModifier('critChance', 'flat', value * stacks, 'buff_crit'),
    ],
  },

  attack_speed: {
    type: 'attack_speed',
    name: 'Hız',
    description: '%{value} artan saldırı hızı',
    duration: 6,
    value: 30,
    maxStacks: 1,
    stackMode: 'refresh',
    isDebuff: false,
    color: '#00ff88',
    createModifiers: (value) => [
      createModifier('attackSpeed', 'increased', value, 'buff_attack_speed'),
    ],
  },

  // Defensive
  armor_boost: {
    type: 'armor_boost',
    name: 'Demir Deri',
    description: '+{flatValue} zırh, %{value} artan zırh',
    duration: 10,
    value: 50,
    flatValue: 100,
    maxStacks: 1,
    stackMode: 'refresh',
    isDebuff: false,
    color: '#888888',
    createModifiers: (value, _stacks, flatValue) => [
      createModifier('armor', 'flat', flatValue || 0, 'buff_armor'),
      createModifier('armor', 'increased', value, 'buff_armor'),
    ],
  },

  resistance_boost: {
    type: 'resistance_boost',
    name: 'Elemental Kalkan',
    description: '+{value} tüm dirençler',
    duration: 12,
    value: 25,
    maxStacks: 1,
    stackMode: 'refresh',
    isDebuff: false,
    color: '#00ffff',
    createModifiers: (value) => [
      createModifier('allResist', 'flat', value, 'buff_resist'),
    ],
  },

  damage_reduction: {
    type: 'damage_reduction',
    name: 'Koruma',
    description: '+{value} zırh (geçici)',
    duration: 5,
    value: 100,
    maxStacks: 1,
    stackMode: 'refresh',
    isDebuff: false,
    color: '#4488ff',
    createModifiers: (value) => [
      createModifier('armor', 'flat', value, 'buff_damage_reduction'),
    ],
  },

  shield: {
    type: 'shield',
    name: 'Enerji Kalkanı',
    description: '{flatValue} hasar emen kalkan',
    duration: 8,
    value: 0,
    flatValue: 200,
    maxStacks: 1,
    stackMode: 'refresh',
    isDebuff: false,
    color: '#8844ff',
    createModifiers: () => [], // Shield is handled separately
  },

  // Utility
  speed_boost: {
    type: 'speed_boost',
    name: 'Rüzgar Hızı',
    description: '%{value} artan hareket hızı',
    duration: 5,
    value: 40,
    maxStacks: 1,
    stackMode: 'refresh',
    isDebuff: false,
    color: '#88ff88',
    createModifiers: (value) => [
      createModifier('moveSpeed', 'increased', value, 'buff_speed'),
    ],
  },

  hp_regen: {
    type: 'hp_regen',
    name: 'Yenilenme',
    description: 'Saniyede {value} can yenileme',
    duration: 10,
    value: 20,
    maxStacks: 1,
    stackMode: 'refresh',
    isDebuff: false,
    color: '#00ff00',
    tickInterval: 1.0,
    createModifiers: () => [], // Regen is handled via tick
  },

  mana_regen: {
    type: 'mana_regen',
    name: 'Mana Akışı',
    description: 'Saniyede {value} mana yenileme',
    duration: 10,
    value: 15,
    maxStacks: 1,
    stackMode: 'refresh',
    isDebuff: false,
    color: '#0088ff',
    tickInterval: 1.0,
    createModifiers: () => [], // Regen is handled via tick
  },

  // Debuffs
  weakness: {
    type: 'weakness',
    name: 'Zayıflık',
    description: '%{value} azalan hasar',
    duration: 6,
    value: 25,
    maxStacks: 1,
    stackMode: 'refresh',
    isDebuff: true,
    color: '#666666',
    createModifiers: (value) => [
      createModifier('damage', 'reduced', value, 'debuff_weakness'),
    ],
  },

  vulnerability: {
    type: 'vulnerability',
    name: 'Savunmasızlık',
    description: '-%{value} zırh',
    duration: 8,
    value: 50,
    maxStacks: 3,
    stackMode: 'stack',
    isDebuff: true,
    color: '#ff0066',
    createModifiers: (value, stacks) => [
      createModifier('armor', 'flat', -(value * stacks), 'debuff_vuln'),
    ],
  },

  silence: {
    type: 'silence',
    name: 'Susturma',
    description: 'Yetenek kullanamaz',
    duration: 3,
    value: 0,
    maxStacks: 1,
    stackMode: 'refresh',
    isDebuff: true,
    color: '#990099',
    createModifiers: () => [], // Silence is checked directly
  },

  // Missing buff types
  penetration: {
    type: 'penetration',
    name: 'Zırh Delme',
    description: '%{value} zırh penetrasyonu',
    duration: 8,
    value: 30,
    maxStacks: 1,
    stackMode: 'refresh',
    isDebuff: false,
    color: '#ff4444',
    createModifiers: (value) => [
      createModifier('armor', 'flat', -value, 'buff_penetration'),
    ],
  },

  blind: {
    type: 'blind',
    name: 'Körlük',
    description: '%{value} azalan isabet',
    duration: 4,
    value: 40,
    maxStacks: 1,
    stackMode: 'refresh',
    isDebuff: true,
    color: '#333333',
    createModifiers: (value) => [
      createModifier('evasion', 'flat', -value, 'debuff_blind'),
    ],
  },

  curse: {
    type: 'curse',
    name: 'Lanet',
    description: '%{value} artan alınan hasar',
    duration: 10,
    value: 20,
    maxStacks: 3,
    stackMode: 'stack',
    isDebuff: true,
    color: '#660066',
    createModifiers: (value, stacks) => [
      createModifier('armor', 'flat', -(value * stacks), 'debuff_curse'),
      createModifier('allResist', 'flat', -(value * stacks), 'debuff_curse'),
    ],
  },

  invulnerable: {
    type: 'invulnerable',
    name: 'Dokunulmazlık',
    description: 'Hasar almaz',
    duration: 3,
    value: 0,
    maxStacks: 1,
    stackMode: 'refresh',
    isDebuff: false,
    color: '#ffffff',
    createModifiers: () => [], // Invulnerable is checked directly
  },
};

// ========================
// Buff Manager
// ========================

export interface BuffUpdateResult {
  hpRegen: number;
  manaRegen: number;
  shieldAmount: number;
  expiredBuffs: string[];
  isSilenced: boolean;
}

export class BuffManager {
  private buffs: Map<string, Buff> = new Map();
  private modifierCache: Modifier[] = [];
  private cacheValid: boolean = false;

  /**
   * Apply a buff from a template
   */
  applyBuff(
    templateId: string,
    sourceId: string,
    sourceType: Buff['sourceType'],
    overrides?: Partial<{ duration: number; value: number; flatValue: number }>
  ): void {
    const template = BUFF_TEMPLATES[templateId];
    if (!template) {
      debugWarn('Buffs', `Unknown buff template: ${templateId}`);
      return;
    }

    const value = overrides?.value ?? template.value;
    const flatValue = overrides?.flatValue ?? template.flatValue;
    const duration = overrides?.duration ?? template.duration;

    this.applyCustomBuff({
      type: template.type,
      name: template.name,
      description: template.description.replace('{value}', value.toString()).replace('{flatValue}', (flatValue || 0).toString()),
      icon: template.icon,
      sourceId,
      sourceType,
      duration,
      value,
      flatValue,
      maxStacks: template.maxStacks,
      stackMode: template.stackMode,
      isDebuff: template.isDebuff,
      color: template.color,
      tickInterval: template.tickInterval,
      createModifiers: (stacks?: number) => template.createModifiers(value, stacks || 1, flatValue),
    });
  }

  /**
   * Apply a custom buff
   */
  applyCustomBuff(config: {
    type: BuffType;
    name: string;
    description: string;
    icon?: string;
    sourceId: string;
    sourceType: Buff['sourceType'];
    duration: number;
    value: number;
    flatValue?: number;
    element?: string;
    maxStacks: number;
    stackMode: 'refresh' | 'stack' | 'independent';
    isDebuff: boolean;
    color?: string;
    tickInterval?: number;
    createModifiers: (stacks?: number) => Modifier[];
  }): void {
    const buffKey = config.stackMode === 'independent'
      ? `${config.type}_${config.sourceId}_${Date.now()}`
      : `${config.type}_${config.sourceId}`;

    const existing = this.buffs.get(buffKey);

    if (existing && config.stackMode !== 'independent') {
      // Handle stacking or refreshing
      if (config.stackMode === 'stack' && existing.stacks < existing.maxStacks) {
        existing.stacks++;
        existing.modifiers = config.createModifiers(existing.stacks);
      }
      existing.remaining = Math.max(existing.remaining, config.duration);
      this.cacheValid = false;
    } else {
      // Create new buff
      const buff: Buff = {
        id: buffKey,
        type: config.type,
        name: config.name,
        description: config.description,
        icon: config.icon,
        sourceId: config.sourceId,
        sourceType: config.sourceType,
        duration: config.duration,
        remaining: config.duration,
        tickInterval: config.tickInterval,
        tickTimer: config.tickInterval,
        value: config.value,
        flatValue: config.flatValue,
        element: config.element,
        stacks: 1,
        maxStacks: config.maxStacks,
        stackMode: config.stackMode,
        modifiers: config.createModifiers(1),
        isDebuff: config.isDebuff,
        color: config.color,
      };

      this.buffs.set(buffKey, buff);
      this.cacheValid = false;
    }
  }

  /**
   * Update all buffs and return effects to apply
   */
  update(deltaTime: number): BuffUpdateResult {
    const result: BuffUpdateResult = {
      hpRegen: 0,
      manaRegen: 0,
      shieldAmount: 0,
      expiredBuffs: [],
      isSilenced: false,
    };

    const toRemove: string[] = [];

    for (const [key, buff] of this.buffs) {
      // Update duration (skip permanent buffs)
      if (buff.duration > 0) {
        buff.remaining -= deltaTime;
        if (buff.remaining <= 0) {
          toRemove.push(key);
          result.expiredBuffs.push(buff.name);
          continue;
        }
      }

      // Process tick effects
      if (buff.tickInterval && buff.tickTimer !== undefined) {
        buff.tickTimer -= deltaTime;
        if (buff.tickTimer <= 0) {
          buff.tickTimer += buff.tickInterval;

          // Apply tick effect
          switch (buff.type) {
            case 'hp_regen':
              result.hpRegen += buff.value * buff.stacks;
              break;
            case 'mana_regen':
              result.manaRegen += buff.value * buff.stacks;
              break;
          }
        }
      }

      // Check for special states
      if (buff.type === 'silence') {
        result.isSilenced = true;
      }

      if (buff.type === 'shield' && buff.flatValue) {
        result.shieldAmount += buff.flatValue * buff.stacks;
      }
    }

    // Remove expired buffs
    for (const key of toRemove) {
      this.buffs.delete(key);
      this.cacheValid = false;
    }

    return result;
  }

  /**
   * Get all modifiers from active buffs
   */
  getModifiers(): Modifier[] {
    if (this.cacheValid) {
      return this.modifierCache;
    }

    this.modifierCache = [];
    for (const buff of this.buffs.values()) {
      this.modifierCache.push(...buff.modifiers);
    }
    this.cacheValid = true;

    return this.modifierCache;
  }

  /**
   * Remove a specific buff
   */
  removeBuff(type: BuffType, sourceId?: string): boolean {
    const toRemove: string[] = [];
    for (const [key, buff] of this.buffs) {
      if (buff.type === type && (!sourceId || buff.sourceId === sourceId)) {
        toRemove.push(key);
      }
    }
    for (const key of toRemove) {
      this.buffs.delete(key);
    }
    if (toRemove.length > 0) {
      this.cacheValid = false;
    }
    return toRemove.length > 0;
  }

  /**
   * Remove all buffs from a source
   */
  removeBuffsFromSource(sourceId: string): void {
    const toRemove: string[] = [];
    for (const [key, buff] of this.buffs) {
      if (buff.sourceId === sourceId) {
        toRemove.push(key);
      }
    }
    for (const key of toRemove) {
      this.buffs.delete(key);
    }
    if (toRemove.length > 0) {
      this.cacheValid = false;
    }
  }

  /**
   * Clear all buffs
   */
  clear(): void {
    this.buffs.clear();
    this.cacheValid = false;
  }

  /**
   * Clear only debuffs
   */
  clearDebuffs(): void {
    const toRemove: string[] = [];
    for (const [key, buff] of this.buffs) {
      if (buff.isDebuff) {
        toRemove.push(key);
      }
    }
    for (const key of toRemove) {
      this.buffs.delete(key);
    }
    if (toRemove.length > 0) {
      this.cacheValid = false;
    }
  }

  /**
   * Check if entity has a specific buff type
   */
  hasBuff(type: BuffType): boolean {
    for (const buff of this.buffs.values()) {
      if (buff.type === type) return true;
    }
    return false;
  }

  /**
   * Get buff by type
   */
  getBuff(type: BuffType): Buff | null {
    for (const buff of this.buffs.values()) {
      if (buff.type === type) return buff;
    }
    return null;
  }

  /**
   * Get all active buffs (for UI display)
   */
  getActiveBuffs(): Buff[] {
    return Array.from(this.buffs.values());
  }

  /**
   * Get only positive buffs
   */
  getPositiveBuffs(): Buff[] {
    return Array.from(this.buffs.values()).filter(b => !b.isDebuff);
  }

  /**
   * Get only debuffs
   */
  getDebuffs(): Buff[] {
    return Array.from(this.buffs.values()).filter(b => b.isDebuff);
  }

  /**
   * Check if silenced
   */
  isSilenced(): boolean {
    return this.hasBuff('silence');
  }

  /**
   * Check if invulnerable
   */
  isInvulnerable(): boolean {
    return this.hasBuff('invulnerable');
  }

  /**
   * Get total shield amount
   */
  getShieldAmount(): number {
    let total = 0;
    for (const buff of this.buffs.values()) {
      if (buff.type === 'shield' && buff.flatValue) {
        total += buff.flatValue * buff.stacks;
      }
    }
    return total;
  }

  /**
   * Damage shield and return remaining damage
   */
  damageShield(damage: number): number {
    const toRemove: string[] = [];
    for (const [key, buff] of this.buffs) {
      if (buff.type === 'shield' && buff.flatValue) {
        if (buff.flatValue >= damage) {
          buff.flatValue -= damage;
          return 0;
        } else {
          damage -= buff.flatValue;
          toRemove.push(key);
        }
      }
    }
    for (const key of toRemove) {
      this.buffs.delete(key);
    }
    if (toRemove.length > 0) {
      this.cacheValid = false;
    }
    return damage;
  }
}

// ========================
// Helper Functions
// ========================

/**
 * Create a simple damage buff
 */
export function createDamageBuff(
  percent: number,
  duration: number,
  sourceId: string
): Parameters<BuffManager['applyCustomBuff']>[0] {
  return {
    type: 'damage_boost',
    name: 'Hasar Artışı',
    description: `%${percent} artan hasar`,
    sourceId,
    sourceType: 'skill',
    duration,
    value: percent,
    maxStacks: 1,
    stackMode: 'refresh',
    isDebuff: false,
    color: '#ff6600',
    createModifiers: () => [createModifier('damage', 'increased', percent, sourceId)],
  };
}

/**
 * Create a simple speed buff
 */
export function createSpeedBuff(
  percent: number,
  duration: number,
  sourceId: string
): Parameters<BuffManager['applyCustomBuff']>[0] {
  return {
    type: 'speed_boost',
    name: 'Hız Artışı',
    description: `%${percent} artan hareket hızı`,
    sourceId,
    sourceType: 'skill',
    duration,
    value: percent,
    maxStacks: 1,
    stackMode: 'refresh',
    isDebuff: false,
    color: '#88ff88',
    createModifiers: () => [createModifier('moveSpeed', 'increased', percent, sourceId)],
  };
}
